import { useContext, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { User, Role } from '@esg/esg-global-types';

import { useUserMutations } from './hooks/useUserMutations';
import { getUsersPage } from '../../graphql/services/user.service';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';

/**
 * Flattened (company → group) option for the assign-to-company picker.
 */
export interface CompanyOption {
  id: string;
  name: string;
  group_id: string;
  group_name: string;
}

const LEFT_PAGE_SIZE = 50;

const initials = (name: string): string =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const UserRow = ({ user }: { user: User }): JSX.Element => (
  <Stack direction="row" spacing={1.25} alignItems="center">
    <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: '#d7e6ea', color: '#2b5560' }}>
      {initials(`${user.first_name} ${user.last_name}`)}
    </Avatar>
    <Box>
      <Typography variant="body2">
        {user.first_name} {user.last_name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {user.email}
      </Typography>
    </Box>
  </Stack>
);

const CompanyRoleFields = ({
  companies,
  roles,
  companyId,
  role,
  onCompany,
  onRole
}: {
  companies: CompanyOption[];
  roles: Role[];
  companyId: string;
  role: string;
  onCompany: (id: string) => void;
  onRole: (roleName: string) => void;
}): JSX.Element => (
  <>
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel>Company</InputLabel>
      <Select label="Company" value={companyId} onChange={(e) => onCompany(e.target.value)}>
        {companies.map((company) => (
          <MenuItem key={company.id} value={company.id}>
            {company.name}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              · {company.group_name}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>Role</InputLabel>
      <Select label="Role" value={role} onChange={(e) => onRole(e.target.value)}>
        {roles
          .filter((roleOption) => roleOption.name.toLowerCase() !== 'custom')
          .map((roleOption) => (
            <MenuItem key={roleOption.id} value={roleOption.name.toLowerCase()}>
              {roleOption.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  </>
);

const Pane = ({
  title,
  rows,
  active,
  setActive,
  search,
  setSearch,
  loading,
  onLoadMore
}: {
  title: string;
  rows: User[];
  active: Set<string>;
  setActive: Dispatch<SetStateAction<Set<string>>>;
  search: string;
  setSearch: (value: string) => void;
  loading?: boolean;
  onLoadMore?: () => void;
}): JSX.Element => (
  <Paper
    variant="outlined"
    sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: 440, minWidth: 260 }}
  >
    <Typography variant="subtitle2" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      {title} ({rows.length})
    </Typography>
    <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </Box>
    <List dense sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {rows.map((user) => (
        <ListItemButton
          key={user.id}
          selected={active.has(user.id)}
          onClick={() =>
            setActive((current) => {
              const next = new Set(current);
              if (next.has(user.id)) {
                next.delete(user.id);
              } else {
                next.add(user.id);
              }
              return next;
            })
          }
        >
          <UserRow user={user} />
        </ListItemButton>
      ))}
      {onLoadMore && (
        <ListItemButton onClick={onLoadMore} disabled={loading}>
          <Typography variant="body2" color="primary">
            {loading ? 'Loading…' : 'Load more'}
          </Typography>
        </ListItemButton>
      )}
    </List>
  </Paper>
);

/**
 * Dual-pane transfer-list dialog for assigning many existing users to a single
 * company/role in one action. Pick a company + role, move users from the
 * searchable "All users" pane into "To assign", then grant via the real
 * `bulkUpdateAccess` mutation.
 *
 * "All users" is a debounced server search (getUsersPage — already scoped to
 * the viewer's admin groups and excludes deleted users server-side) rather
 * than a client-side filter over a preloaded list, so it finds a match
 * regardless of how many users exist. "To assign" is a plain Map of picked
 * users, independent of the current search term, so a pick made under one
 * search term survives the admin searching for something else afterwards.
 *
 * Controlled: the host owns `open`/`onClose` and supplies the flattened
 * company options and roles. `onDone` fires after a successful grant so the
 * host can refresh its row data.
 */
const AssignUsersToCompanyDialog = ({
  open,
  onClose,
  companies,
  roles,
  onDone
}: {
  open: boolean;
  onClose: () => void;
  companies: CompanyOption[];
  roles: Role[];
  onDone: () => void | Promise<void>;
}): JSX.Element => {
  const mutations = useUserMutations();
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [companyId, setCompanyId] = useState('');
  const [role, setRole] = useState('viewer');
  const [busy, setBusy] = useState(false);
  const [assigned, setAssigned] = useState<Map<string, User>>(new Map());
  const [activeLeft, setActiveLeft] = useState<Set<string>>(new Set());
  const [activeRight, setActiveRight] = useState<Set<string>>(new Set());
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');

  const [leftUsers, setLeftUsers] = useState<User[]>([]);
  const [leftLoading, setLeftLoading] = useState(false);
  const [leftCursor, setLeftCursor] = useState<string | null>(null);
  // Sequences every "All users" fetch — the debounced search effect below and
  // loadMoreLeft both bump this before their request and compare it after, so
  // a response that's been superseded by a newer search or another Load-more
  // click is dropped instead of appending/overwriting with stale data (which
  // previously could interleave a stale search's rows into the current list,
  // with duplicate `key={user.id}` collisions).
  const leftRequestIdRef = useRef(0);

  useEffect(() => {
    if (open) {
      setCompanyId('');
      setRole('viewer');
      setAssigned(new Map());
      setActiveLeft(new Set());
      setActiveRight(new Set());
      setSearchLeft('');
      setSearchRight('');
      setLeftUsers([]);
      setLeftCursor(null);
    }
  }, [open]);

  // Debounced server search for "All users" — re-runs from a fresh first page
  // whenever the dialog opens or the search term changes. Super admins already
  // have access to every company/group, so there is nothing to assign — keep
  // them out of the list entirely (the server has no such filter, so this is
  // applied client-side over the returned page).
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(
      async () => {
        const requestId = ++leftRequestIdRef.current;
        setLeftLoading(true);
        try {
          const page = await getUsersPage(
            { search: searchLeft.trim() || undefined, include_deleted: false },
            null,
            LEFT_PAGE_SIZE
          );
          if (requestId !== leftRequestIdRef.current) return; // superseded
          setLeftUsers(page.users.filter((u) => !u.super_admin));
          setLeftCursor(page.nextCursor);
        } catch (err: unknown) {
          if (requestId !== leftRequestIdRef.current) return;
          setLeftUsers([]);
          setLeftCursor(null);
          setFeedbackData({
            message:
              err instanceof Error
                ? `Unable to fetch users: ${err.message}`
                : 'Unable to fetch users.',
            state: true,
            type: 'error'
          });
        } finally {
          if (requestId === leftRequestIdRef.current) setLeftLoading(false);
        }
      },
      searchLeft ? 300 : 0
    );
    return () => clearTimeout(timer);
  }, [open, searchLeft, setFeedbackData]);

  const loadMoreLeft = async () => {
    if (!leftCursor) return;
    const requestId = ++leftRequestIdRef.current;
    setLeftLoading(true);
    try {
      const page = await getUsersPage(
        { search: searchLeft.trim() || undefined, include_deleted: false },
        leftCursor,
        LEFT_PAGE_SIZE
      );
      if (requestId !== leftRequestIdRef.current) return; // superseded
      setLeftUsers((prev) => [...prev, ...page.users.filter((u) => !u.super_admin)]);
      setLeftCursor(page.nextCursor);
    } catch (err: unknown) {
      if (requestId !== leftRequestIdRef.current) return;
      setFeedbackData({
        message:
          err instanceof Error
            ? `Unable to load more users: ${err.message}`
            : 'Unable to load more users.',
        state: true,
        type: 'error'
      });
    } finally {
      if (requestId === leftRequestIdRef.current) setLeftLoading(false);
    }
  };

  const company = companies.find((option) => option.id === companyId);

  const grant = async (ids: string[]): Promise<boolean> => {
    if (!company || ids.length === 0) return false;
    setBusy(true);
    const result = await mutations.bulkUpdateAccess({
      ids,
      target_group_id: company.group_id,
      target_company_id: company.id,
      role
    });
    setBusy(false);
    if (!result) return false;
    await onDone();
    return true;
  };

  const matchRight = (user: User, search: string) =>
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
    (user.email ?? '').toLowerCase().includes(search.toLowerCase());

  const left = leftUsers.filter((user) => !assigned.has(user.id));
  const right = [...assigned.values()].filter((user) => matchRight(user, searchRight));

  const move = (from: Set<string>, dir: 'r' | 'l') => {
    if (dir === 'r') {
      setAssigned((current) => {
        const next = new Map(current);
        from.forEach((id) => {
          const found = leftUsers.find((u) => u.id === id);
          if (found) next.set(id, found);
        });
        return next;
      });
      setActiveLeft(new Set());
    } else {
      setAssigned((current) => {
        const next = new Map(current);
        from.forEach((id) => next.delete(id));
        return next;
      });
      setActiveRight(new Set());
    }
  };
  const doAssign = async () => {
    const ok = await grant([...assigned.keys()]);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign users to a company</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 1 }}>
          <CompanyRoleFields
            companies={companies}
            roles={roles}
            companyId={companyId}
            role={role}
            onCompany={setCompanyId}
            onRole={setRole}
          />
        </Stack>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Pane
            title="All users"
            rows={left}
            active={activeLeft}
            setActive={setActiveLeft}
            search={searchLeft}
            setSearch={setSearchLeft}
            loading={leftLoading}
            onLoadMore={leftCursor ? loadMoreLeft : undefined}
          />
          <Stack spacing={1}>
            <IconButton
              disabled={activeLeft.size === 0}
              onClick={() => move(activeLeft, 'r')}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <ChevronRightIcon />
            </IconButton>
            <IconButton
              disabled={activeRight.size === 0}
              onClick={() => move(activeRight, 'l')}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Stack>
          <Pane
            title="To assign"
            rows={right}
            active={activeRight}
            setActive={setActiveRight}
            search={searchRight}
            setSearch={setSearchRight}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={assigned.size === 0 || !companyId || busy}
          onClick={doAssign}
        >
          Assign {assigned.size} user{assigned.size === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUsersToCompanyDialog;
